import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Formik } from 'formik';
import queryString, { ParsedQuery } from 'query-string';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import * as Yup from 'yup';
import { Box, styled, useTheme } from '@mui/material';

import {
  Button,
  PasswordField,
  TextField,
  Typography,
} from '@zendesk/forethought-elements';
import omit from 'lodash/fp/omit';
import lineSvg from 'src/assets/images/line.svg';
import LandingPageLayout from 'src/components/reusable-components/landing-page-layout';
import { Routes } from 'src/utils/enums';
import { storePreAuthParams } from 'src/utils/sessionUtils';
import { hasErrorMessage } from 'src/utils/typeGuards';

interface LoginFormState {
  email: string;
  password: string;
}

interface LoginProps {
  authValidationError?: string;
  isSSOEnabled: boolean;
  isSSOEnforced: boolean;
}

export default function Login({
  authValidationError,
  isSSOEnabled,
  isSSOEnforced,
}: LoginProps) {
  const { t } = useTranslation('authentication-wall');
  const navigate = useNavigate();
  const location = useLocation();
  const { palette } = useTheme();

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('common.fieldRequired'))
      .min(12, t('common.passwordMinLength')),
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [initialHashParams, setInitialHashParams] =
    useState<ParsedQuery<string> | null>(null);

  const error = errorMessage || authValidationError;

  useEffect(() => {
    const hashParams = queryString.parse(location.hash);

    if (hashParams.username) {
      setInitialHashParams(hashParams);
      // remove username and newUserPrompt params
      const redirectHashParams = omit(
        ['username', 'newUserPrompt'],
        hashParams,
      );
      navigate(
        {
          ...location,
          hash: queryString.stringify(redirectHashParams, { encode: false }),
        },
        { replace: true },
      );
    }
    setInitialLoading(false);
  }, [navigate, location]);

  const handleSubmit = async ({ email, password }: LoginFormState) => {
    setIsLoading(true);
    try {
      await Auth.signIn(email, password);
    } catch (error) {
      let message = t('common.genericErrorTryAgain');
      if (hasErrorMessage(error)) {
        message = error.message;
      }
      setErrorMessage(message);
      setIsLoading(false);
    }
  };

  const handleSSOClick = () => {
    storePreAuthParams();
    navigate(Routes.SINGLE_SIGN_ON);
  };

  const { newUserPrompt, username } = initialHashParams || {};

  const initialValues: LoginFormState = {
    email: typeof username === 'string' ? username : '',
    password: '',
  };

  return (
    <LandingPageLayout testId='login-container'>
      <Container>
        {!initialLoading && (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ errors, handleChange, handleSubmit, values }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Box
                    sx={{
                      mb: '16px',
                    }}
                  >
                    <Typography variant='font32'>
                      {t('common.signInToForethought')}
                    </Typography>
                    {newUserPrompt && username && (
                      <Box
                        sx={{
                          mt: '16px',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant='font16'>
                          <Trans
                            components={{ b: <b /> }}
                            i18nKey='login.temporaryPasswordSentTo'
                            ns='authentication-wall'
                            values={{ username }}
                          />
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!isSSOEnforced && (
                    <>
                      <TextField
                        aria-label={t('common.email')}
                        name='email'
                        onChange={(...args) => {
                          setErrorMessage('');
                          handleChange(...args);
                        }}
                        placeholder={t('common.enterYourEmail')}
                        type='text'
                        value={values.email}
                      />
                      <PasswordField
                        aria-label={t('common.password')}
                        error={errors.password}
                        name='password'
                        onChange={(...args) => {
                          setErrorMessage('');
                          handleChange(...args);
                        }}
                        placeholder={t('common.password')}
                        type='password'
                        value={values.password}
                      />

                      <ButtonContainer>
                        {error && (
                          <Box>
                            <Typography
                              color={palette.colors.red[500]}
                              variant='font16'
                            >
                              {error}
                            </Typography>
                          </Box>
                        )}
                        <Button
                          fullWidth
                          isLoading={isLoading}
                          size='large'
                          type='submit'
                          variant='main'
                        >
                          {t('login.submit')}
                        </Button>
                        <Button
                          onClick={() => navigate(Routes.FORGOT_PASSWORD)}
                          size='large'
                          variant='ghost'
                        >
                          {t('login.forgotPassword')}
                        </Button>
                      </ButtonContainer>
                    </>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
        {isSSOEnabled && (
          <>
            <Box
              sx={{
                color: '#aebecd',
                display: 'flex',
                fontSize: 'var(--font-size-s)',
                justifyContent: 'center',
                padding: '10px',
                textTransform: 'capitalize',
              }}
            >
              {!isSSOEnforced && (
                <>
                  <Line /> {t('login.or')}
                  <Line />
                </>
              )}
            </Box>
            <Button onClick={handleSSOClick} size='large' variant='main'>
              {t('login.ssoButton')}
            </Button>
          </>
        )}
      </Container>
    </LandingPageLayout>
  );
}

const Container = styled('div')`
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Line = styled('div')`
  background: url(${lineSvg}) 0 center no-repeat;
  background-size: cover;
  height: 1px;
  margin: auto;
  width: 100%;
`;
